import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import { MidiaRenderer } from './MidiaMensagem';
import {
  acaoBolhaStyle, anexoItemStyle, QuotedPreviewBox,
  ContatoCard, LocalizacaoCard, ContatoPickerModal, AcoesMensagemMobile, formatNumero,
  faixaDoToque, useEmojisRapidos, reagirEAnotar, TextoDaMensagem,
} from './ChatShared';
import { alertarErro } from '../helpers/alerts';
import EditorImagem, { daPraEditar } from './EditorImagem';

function iniciais(nome) {
  const p = String(nome || '').trim().split(/\s+/).slice(0, 2);
  return p.map((x) => x[0]?.toUpperCase()).join('') || '#';
}
function hora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
const CORES = ['#57A94F', '#F5A623', '#4CB5C0', '#B07BCC', '#E5748B', '#6B9BE5', '#E0993E'];
function corDoNome(nome) {
  let h = 0; const s = String(nome || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 997;
  return CORES[h % CORES.length];
}
const EH_TOUCH = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(hover: none), (pointer: coarse)').matches;

// Check de entrega (igual WhatsApp): ✓ enviada, ✓✓ entregue, ✓✓ azul lida.
// O status vem do ack do WhatsApp via webhook (messages.update) na tabela de grupo.
function StatusGrupo({ status }) {
  if (!status || status === 'erro') return null;
  const lida = status === 'lida';
  const entregue = status === 'entregue';
  const icon = entregue || lida ? 'bi-check2-all' : 'bi-check2';
  const label = lida ? 'Lida' : entregue ? 'Entregue' : 'Enviada';
  return (
    <i className={`bi ${icon}`} title={label} aria-label={label}
      style={{ color: lida ? '#22e3ff' : 'rgba(255,255,255,0.78)', fontSize: '0.95rem', lineHeight: 1 }} />
  );
}

// Balão de mensagem do grupo — mesmas ações da conversa 1:1 (responder, editar,
// apagar, copiar, reagir), swipe-to-reply no mobile e menu de ações em overlay.
function GroupBubble({
  mensagem, onAbrirImagem, onReagir, onResponder, onEditar, onApagar, onAbrirAcoes, isMobile,
  onIrParaMensagem, destacada,
}) {
  const [hovered, setHovered] = useState(false);
  const [emojisAbertos, setEmojisAbertos] = useState(false);
  // Mesma régua do toque longo: os emojis da PESSOA, não os seis fixos.
  const rapidosDoMouse = useEmojisRapidos();
  const [editando, setEditando] = useState(false);
  const [textoEdit, setTextoEdit] = useState('');
  const [dragX, setDragX] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const longPressRef = useRef(null);

  const m = mensagem;
  const out = m.direcao === 'out';
  const quem = out ? (m.autor || 'você') : (m.participante_nome || 'participante');
  const ehMidia = ['imagem', 'audio', 'video', 'documento', 'figurinha'].includes(m.tipo);
  const podeResponder = m.tipo !== 'sistema';

  const cancelarLongPress = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
  };
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    if (!m.apagada && onAbrirAcoes) {
      const bolha = e.currentTarget;
      const toqueY = e.touches[0].clientY;
      longPressRef.current = setTimeout(() => {
        onAbrirAcoes(m, faixaDoToque(bolha, toqueY));
      }, 420);
    }
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) cancelarLongPress();
    if (!podeResponder || m.apagada) return;
    if (dragX === 0 && Math.abs(dy) > Math.abs(dx)) return;
    setDragX(dx > 0 ? Math.min(dx * 0.6, 72) : 0);
  };
  const onTouchEnd = () => {
    cancelarLongPress();
    if (dragX > 45 && podeResponder && !m.apagada) onResponder(m);
    setDragX(0);
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const bg = out ? 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)' : 'var(--portal-bubble-in)';

  return (
    <div
      style={{ position: 'relative', touchAction: 'pan-y' }}
      id={`msg-grupo-${m.id}`}
      className={`${isMobile ? 'balao-toque' : ''}${destacada ? ' msg-destacada' : ''}`.trim() || undefined}
      onContextMenu={isMobile ? (e) => e.preventDefault() : undefined}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setEmojisAbertos(false); }}
    >
      {dragX > 4 && (
        <div style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', opacity: Math.min(dragX / 45, 1), color: '#3D8B37', fontSize: '1.2rem', zIndex: 0 }}>
          <i className="bi bi-reply-fill" />
        </div>
      )}
      <div style={{
        display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', alignItems: 'center', marginBottom: 6,
        transform: dragX ? `translateX(${dragX}px)` : undefined, transition: dragX ? 'none' : 'transform 0.15s ease',
      }}>
        <div style={{
          position: 'relative', maxWidth: '82%', padding: '7px 11px', borderRadius: 12,
          borderTopRightRadius: out ? 4 : 12, borderTopLeftRadius: out ? 12 : 4,
          background: bg, color: out ? '#fff' : 'var(--portal-text)', fontSize: '0.9rem',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          ...(isMobile ? { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } : null),
        }}>
          {hovered && !isMobile && !editando && !m.apagada && (
            <div style={{
              position: 'absolute', zIndex: 20, top: '50%', transform: 'translateY(-50%)',
              [out ? 'right' : 'left']: 'calc(100% + 6px)', display: 'flex', alignItems: 'center', gap: 1,
              padding: '2px 4px', background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
              borderRadius: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}>
              {emojisAbertos ? (
                rapidosDoMouse.map((e) => (
                  <button key={e} type="button" onClick={() => { reagirEAnotar(onReagir, m, e); setEmojisAbertos(false); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.05rem', lineHeight: 1, padding: '2px 3px' }}>{e}</button>
                ))
              ) : (
                <>
                  <button type="button" title="Reagir" onClick={() => setEmojisAbertos(true)} style={acaoBolhaStyle}><i className="bi bi-emoji-smile" /></button>
                  {podeResponder && <button type="button" title="Responder" onClick={() => onResponder(m)} style={acaoBolhaStyle}><i className="bi bi-reply-fill" /></button>}
                  {m.conteudo && <button type="button" title="Copiar" onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(m.conteudo).catch(() => {}); }} style={acaoBolhaStyle}><i className="bi bi-copy" /></button>}
                  {out && m.tipo === 'texto' && (
                    <button type="button" title="Editar" onClick={() => { setTextoEdit(m.conteudo || ''); setEditando(true); }} style={acaoBolhaStyle}><i className="bi bi-pencil" /></button>
                  )}
                  {out && <button type="button" title="Apagar" onClick={() => onApagar(m)} style={acaoBolhaStyle}><i className="bi bi-trash" /></button>}
                </>
              )}
            </div>
          )}

          {(m.quoted_id || m.quoted_preview) && (
            <QuotedPreviewBox
              autor={m.quoted_autor}
              preview={m.quoted_preview ? String(m.quoted_preview).slice(0, 120) : '[mensagem]'}
              onIr={m.quoted_id && onIrParaMensagem ? () => onIrParaMensagem(m.quoted_id) : undefined}
            />
          )}

          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: out ? 'rgba(255,255,255,0.85)' : corDoNome(quem), marginBottom: 1 }}>{quem}</div>

          {editando ? (
            <div style={{ minWidth: 210 }}>
              <textarea value={textoEdit} onChange={(e) => setTextoEdit(e.target.value)} rows={2} autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (textoEdit.trim()) { onEditar(m, textoEdit.trim()); setEditando(false); } }
                  if (e.key === 'Escape') setEditando(false);
                }}
                style={{ width: '100%', resize: 'none', borderRadius: 8, border: 'none', padding: '6px 8px', fontSize: '0.9rem', color: '#222' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 4 }}>
                <button type="button" onClick={() => setEditando(false)} style={{ background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: '0.8rem' }}>Cancelar</button>
                <button type="button" onClick={() => { if (textoEdit.trim()) { onEditar(m, textoEdit.trim()); setEditando(false); } }} style={{ background: '#fff', border: 'none', color: '#2E6A29', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>Salvar</button>
              </div>
            </div>
          ) : m.apagada ? (
            <div style={{ fontStyle: 'italic', opacity: 0.75, display: 'flex', alignItems: 'center', gap: 5 }}>
              <i className="bi bi-slash-circle" /> Mensagem apagada
            </div>
          ) : m.tipo === 'contato' ? (
            <ContatoCard conteudo={m.conteudo} out={out} />
          ) : m.tipo === 'localizacao' ? (
            <LocalizacaoCard conteudo={m.conteudo} out={out} />
          ) : (
            <>
              {ehMidia && <MidiaRenderer mensagem={m} onAbrirImagem={onAbrirImagem} out={out} />}
              {/* Link vira link também no grupo — é o caso do endereço de obra
                  que alguém cola ali. Telefone NÃO vira toque aqui de
                  propósito: "conversar com este número" significaria sair do
                  grupo pra outra tela, e é melhor não oferecer do que oferecer
                  pela metade. */}
              {ehMidia && m.conteudo && (
                <div style={{ marginTop: 4 }}>
                  <TextoDaMensagem texto={m.conteudo} corLink={out ? '#dff3dd' : '#3D8B37'} />
                </div>
              )}
              {!ehMidia && (
                <div>
                  {m.conteudo
                    ? <TextoDaMensagem texto={m.conteudo} corLink={out ? '#dff3dd' : '#3D8B37'} />
                    : '[mensagem]'}
                </div>
              )}
            </>
          )}

          {m.reacao && !m.apagada && (
            <div style={{ marginTop: 3 }}>
              <span style={{ background: out ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.06)', borderRadius: 12, padding: '1px 7px', fontSize: '0.95rem', lineHeight: 1.4 }}>{m.reacao}</span>
            </div>
          )}

          <div style={{ fontSize: '0.66rem', opacity: 0.7, textAlign: 'right', marginTop: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
            {m.editada && !m.apagada && <span style={{ fontStyle: 'italic' }}>editada</span>}
            <span>{hora(m.criada_em)}{m.status === 'erro' ? ' · falha ao enviar' : ''}</span>
            {out && !m.apagada && <StatusGrupo status={m.status} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// Grupos de WhatsApp do canal — chat completo, separado da fila de atendimento.
export default function GruposModal({
  aberto, onClose, onLido, onAbrirImagem, onAbrirEquipe, equipeNaoLidas = 0,
  comoTela = false, onAtivoChange,
}) {
  const [grupos, setGrupos] = useState([]);
  const [ativo, setAtivo] = useState(null); // null (lista) | grupo

  // Como tela, quem hospeda precisa saber se está na lista ou dentro de um
  // grupo — é o que decide sumir com a barra de abas e ir a tela cheia.
  const aoMudarAtivo = useRef(onAtivoChange);
  useEffect(() => { aoMudarAtivo.current = onAtivoChange; });
  useEffect(() => { aoMudarAtivo.current?.(ativo); }, [ativo]);

  // Mesma história da conversa 1:1: entrar num grupo é estado, não navegação,
  // e sem isto o "voltar" do aparelho pula a tela inteira em vez de sair do
  // grupo. Só como TELA — como modal, quem fecha é o fundo.
  useEffect(() => {
    if (!comoTela || !EH_TOUCH || !ativo) return undefined;
    let fechouPeloVoltar = false;
    window.history.pushState({ fioGrupo: true }, '');
    const aoVoltar = () => { fechouPeloVoltar = true; setAtivo(null); };
    window.addEventListener('popstate', aoVoltar);
    return () => {
      window.removeEventListener('popstate', aoVoltar);
      if (!fechouPeloVoltar && window.history.state?.fioGrupo) window.history.back();
    };
  }, [comoTela, ativo]);
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [naoLidas, setNaoLidas] = useState({}); // { grupoId: total }
  const [arquivo, setArquivo] = useState(null);
  const [editandoImagem, setEditandoImagem] = useState(null);
  const [gravando, setGravando] = useState(false);
  const [tempoGrav, setTempoGrav] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [anexoMenu, setAnexoMenu] = useState(false);

  // Menu do "+" fecha ao clicar fora (ou ESC), igual qualquer dropdown. O botão
  // fica DENTRO do ref, então clicar nele continua alternando normalmente.
  const anexoMenuRef = useRef(null);
  useEffect(() => {
    if (!anexoMenu) return undefined;
    const cliqueFora = (e) => {
      if (!anexoMenuRef.current?.contains(e.target)) setAnexoMenu(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setAnexoMenu(false); };
    document.addEventListener('pointerdown', cliqueFora);
    window.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('pointerdown', cliqueFora);
      window.removeEventListener('keydown', onEsc);
    };
  }, [anexoMenu]);

  const [pickerContato, setPickerContato] = useState(false);
  const [acaoMensagem, setAcaoMensagem] = useState(null);
  const [acaoAncora, setAcaoAncora] = useState(null);
  const [destaqueId, setDestaqueId] = useState(null);
  const destaqueTimer = useRef(null);
  const irParaMensagem = (id) => {
    const alvo = document.getElementById(`msg-grupo-${id}`);
    if (!alvo) return; // original fora do trecho carregado
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setDestaqueId(id);
    clearTimeout(destaqueTimer.current);
    destaqueTimer.current = setTimeout(() => setDestaqueId(null), 1600);
  };
  useEffect(() => () => clearTimeout(destaqueTimer.current), []); // menu de ações mobile
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const ultimoMarcadoRef = useRef(0);
  const inputArqRef = useRef(null);
  // Input próprio pra câmera (o `capture` mora no elemento — pôr no do clipe
  // faria o clipe abrir a câmera em vez de deixar escolher arquivo).
  const inputCamRef = useRef(null);
  const textareaRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const canceladoRef = useRef(false);
  const timerGravRef = useRef(null);

  const carregarNaoLidas = () => {
    axios.get(`${API}/grupos/nao-lidas`).then((r) => {
      const map = {};
      (r.data?.data?.porGrupo || []).forEach((x) => { map[x.grupo_id] = Number(x.total || 0); });
      setNaoLidas(map);
    }).catch(() => {});
  };

  // Lista de grupos ao abrir (sincroniza da Evolution no backend).
  useEffect(() => {
    if (!aberto) return;
    setAtivo(null);
    setCarregandoLista(true);
    axios.get(`${API}/grupos`).then((r) => setGrupos(r.data?.data || []))
      .catch(() => {}).finally(() => setCarregandoLista(false));
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return undefined;
    carregarNaoLidas();
    const t = setInterval(carregarNaoLidas, 10000);
    return () => clearInterval(t);
  }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mensagens do grupo ativo + polling + marcar lido.
  useEffect(() => {
    if (!aberto || !ativo) { setMensagens([]); return undefined; }
    ultimoMarcadoRef.current = 0;
    const marcar = (msgs) => {
      // id é BIGINT (vem como string do banco); temp-... vira NaN e é ignorado.
      const maxId = msgs.reduce((mx, m) => {
        const n = Number(m.id);
        return (Number.isFinite(n) && n > mx) ? n : mx;
      }, 0);
      if (!maxId || maxId <= ultimoMarcadoRef.current) return;
      ultimoMarcadoRef.current = maxId;
      axios.post(`${API}/grupos/${ativo.id}/lida`, { ate: maxId })
        .then(() => { carregarNaoLidas(); onLido?.(); }).catch(() => {});
    };
    const carregar = () => axios.get(`${API}/grupos/${ativo.id}/mensagens`)
      .then((r) => { const d = r.data?.data || []; setMensagens(d); marcar(d); }).catch(() => {});
    carregar();
    pollRef.current = setInterval(carregar, 4000);
    return () => clearInterval(pollRef.current);
  }, [aberto, ativo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensagens.length, ativo]);

  // Ao trocar de grupo, limpa estados de composição.
  useEffect(() => {
    setReplyTo(null); setAnexoMenu(false); setArquivo(null); setEditandoImagem(null);
  }, [ativo]);

  const recarregar = () => axios.get(`${API}/grupos/${ativo.id}/mensagens`).then((r) => setMensagens(r.data?.data || [])).catch(() => {});

  const enviar = async (e) => {
    e?.preventDefault?.();
    const t = texto.trim();
    if ((!t && !arquivo) || !ativo || enviando) return;
    setEnviando(true);
    const reply = replyTo;
    try {
      if (arquivo) {
        const fd = new FormData();
        fd.append('arquivo', arquivo);
        if (t) fd.append('texto', t);
        if (reply) fd.append('quotedMensagemId', String(reply.id));
        await axios.post(`${API}/grupos/${ativo.id}/mensagens`, fd);
        setArquivo(null);
      } else {
        await axios.post(`${API}/grupos/${ativo.id}/mensagens`, { texto: t, quotedMensagemId: reply ? reply.id : undefined });
      }
      setTexto('');
      setReplyTo(null);
      await recarregar();
    } catch (err) {
      setTexto((cur) => cur || t);
      await recarregar(); // mídia com falha aparece com status "falha ao enviar"
      alertarErro(err, 'Não deu pra enviar');
    } finally { setEnviando(false); }
  };

  const reagir = async (msg, emoji) => {
    setMensagens((ms) => ms.map((x) => (x.id === msg.id ? { ...x, reacao: emoji || null } : x)));
    try {
      await axios.post(`${API}/grupos/${ativo.id}/mensagens/${msg.id}/reagir`, { reaction: emoji });
    } catch (e) { recarregar(); }
  };

  const responder = (msg) => { setReplyTo(msg); setTimeout(() => textareaRef.current?.focus(), 50); };

  const editarInline = async (msg, novoTexto) => {
    try {
      await axios.put(`${API}/grupos/${ativo.id}/mensagens/${msg.id}`, { texto: novoTexto });
      await recarregar();
    } catch (e) { alertarErro(e, 'Não deu pra editar'); }
  };
  const editarPrompt = async (msg) => {
    const { value } = await Swal.fire({
      title: 'Editar mensagem', input: 'textarea', inputValue: msg.conteudo || '',
      showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar', confirmButtonColor: '#3D8B37',
    });
    if (value != null && value.trim() && value.trim() !== (msg.conteudo || '')) editarInline(msg, value.trim());
  };
  const apagar = async (msg) => {
    const r = await Swal.fire({
      icon: 'warning', title: 'Apagar mensagem?', text: 'Ela some pra todos no grupo do WhatsApp.',
      showCancelButton: true, confirmButtonText: 'Apagar', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc3545',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/grupos/${ativo.id}/mensagens/${msg.id}`);
      await recarregar();
    } catch (e) { alertarErro(e, 'Não deu pra apagar'); }
  };
  const copiar = (msg) => {
    const txt = msg.conteudo || '';
    if (navigator.clipboard) navigator.clipboard.writeText(txt).catch(() => {});
  };

  // Contato = escolher da agenda, com confirmação (igual 1:1).
  const anexarContato = () => { setAnexoMenu(false); setPickerContato(true); };
  const enviarContatoEscolhido = async (c) => {
    setPickerContato(false);
    const nome = c.push_name || formatNumero(c.numero) || 'Contato';
    const r = await Swal.fire({
      title: 'Enviar este contato?',
      html: `<div style="font-size:1.05rem;font-weight:700">${nome}</div>`
        + `<div style="color:var(--portal-muted,#888)">${formatNumero(c.numero)}</div>`,
      icon: 'question', showCancelButton: true, confirmButtonText: 'Enviar', cancelButtonText: 'Cancelar', confirmButtonColor: '#3D8B37',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.post(`${API}/grupos/${ativo.id}/contato`, { fullName: nome, phoneNumber: c.numero });
      await recarregar();
    } catch (e) { alertarErro(e, 'Não deu pra salvar'); }
  };

  // Localização = pega a atual do navegador, com pré-visualização (igual 1:1).
  const anexarLocalizacao = () => {
    setAnexoMenu(false);
    if (!navigator.geolocation) { Swal.fire({ icon: 'error', title: 'Sem GPS', text: 'Seu navegador não suporta localização.' }); return; }
    Swal.fire({ title: 'Pegando sua localização...', didOpen: () => Swal.showLoading(), allowOutsideClick: false });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude; const lng = pos.coords.longitude;
        Swal.close();
        const r = await Swal.fire({
          title: 'Enviar esta localização?',
          html: `<iframe title="mapa" width="100%" height="220" style="border:0;border-radius:10px" loading="lazy" src="https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed"></iframe>`,
          width: 460, showCancelButton: true, confirmButtonText: 'Enviar', cancelButtonText: 'Cancelar', confirmButtonColor: '#3D8B37',
        });
        if (!r.isConfirmed) return;
        try {
          await axios.post(`${API}/grupos/${ativo.id}/localizacao`, { latitude: lat, longitude: lng, name: 'Localização atual' });
          await recarregar();
        } catch (e) { alertarErro(e, 'Não deu pra enviar'); }
      },
      (err) => {
        Swal.fire({ icon: 'error', title: 'Não consegui pegar a localização', text: err.code === 1 ? 'Permissão negada — autorize a localização no navegador.' : 'Tente de novo.' });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const escolherArquivo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    // Aqui não havia limite NENHUM — a conversa 1:1 barrava em 25MB e o grupo
    // aceitava qualquer coisa. Mesma trava, mesmo editor: foto de grupo sofre
    // do mesmo upload pesado que fazia o envio morrer no meio.
    if (f.size > 25 * 1024 * 1024) {
      Swal.fire({ icon: 'warning', title: 'Arquivo muito grande', text: 'Limite de 25MB.' });
      return;
    }
    if (daPraEditar(f)) { setEditandoImagem(f); return; }
    setArquivo(f);
    // O foco ficava no clipe e o Enter reabria o seletor de arquivo; joga o
    // cursor pro campo de texto pra que Enter envie.
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  async function iniciarGravacao() {
    if (!navigator.mediaDevices?.getUserMedia) return;
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { return; }
    const mr = new MediaRecorder(stream);
    chunksRef.current = [];
    canceladoRef.current = false;
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerGravRef.current);
      setGravando(false); setTempoGrav(0);
      if (canceladoRef.current || !chunksRef.current.length || !ativo) return;
      const tipoMime = mr.mimeType || 'audio/webm';
      const ext = tipoMime.includes('ogg') ? 'ogg' : 'webm';
      const file = new File([new Blob(chunksRef.current, { type: tipoMime })], `audio_${Date.now()}.${ext}`, { type: tipoMime });
      setEnviando(true);
      try {
        const fd = new FormData(); fd.append('arquivo', file);
        await axios.post(`${API}/grupos/${ativo.id}/mensagens`, fd);
        await recarregar();
      } catch (e) {
        await recarregar(); // mostra a msg com status "falha ao enviar"
        alertarErro(e, 'Não deu pra enviar o áudio');
      } finally { setEnviando(false); }
    };
    mr.start();
    mrRef.current = mr;
    setGravando(true); setTempoGrav(0);
    timerGravRef.current = setInterval(() => setTempoGrav((t) => t + 1), 1000);
  }
  const pararEnviar = () => { canceladoRef.current = false; if (mrRef.current?.state === 'recording') mrRef.current.stop(); };
  const cancelarGrav = () => { canceladoRef.current = true; if (mrRef.current?.state === 'recording') mrRef.current.stop(); };
  const fmtGrav = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (!aberto) return null;
  const badge = (n) => (n > 0 ? (
    <span style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: '#e5484d', color: '#fff', fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0 }}>{n > 99 ? '99+' : n}</span>
  ) : null);

  // Como TELA, o cartão ocupa a página inteira e não existe fundo pra clicar e
  // fechar. Como MODAL, continua flutuando por cima do que estava aberto.
  const corpo = (
      <div
        className="content-card"
        onClick={comoTela ? undefined : (e) => e.stopPropagation()}
        style={comoTela
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }
          : { width: 'min(480px, 96vw)', height: 'min(80vh, 640px)', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, padding: '12px 14px', borderBottom: '1px solid var(--portal-border)' }}>
          {ativo ? (
            <button type="button" onClick={() => setAtivo(null)} aria-label="Voltar" style={{ background: 'transparent', border: 'none', color: 'var(--portal-text)', fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1, padding: '2px 4px' }}><i className="bi bi-arrow-left" /></button>
          ) : (<i className="bi bi-people-fill" style={{ color: '#3D8B37', fontSize: '1.2rem' }} />)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{ativo ? ativo.subject : 'Grupos'}</strong>
            {!ativo && <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>Grupos de WhatsApp — não é a fila de atendimento.</div>}
            {ativo && ativo.tamanho ? <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>{ativo.tamanho} participantes</div> : null}
          </div>
          {!comoTela && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}><i className="bi bi-x-lg" /></button>
          )}
        </header>

        {!ativo ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* A Equipe é a mesma natureza dos grupos — conversa de várias
                pessoas, fora da fila do cliente — então mora aqui, no topo e
                destacada, em vez de gastar uma aba só dela. */}
            {onAbrirEquipe && (
              <button
                type="button"
                onClick={onAbrirEquipe}
                style={{
                  ...listaItemStyle,
                  background: 'var(--portal-surface-soft)',
                  borderBottom: '2px solid var(--portal-border)',
                }}
              >
                <div style={{ ...avatarStyle, background: '#3D8B37' }}>
                  <i className="bi bi-chat-square-text-fill" style={{ fontSize: '1.05rem' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <strong style={{ fontSize: '0.9rem', display: 'block' }}>Equipe</strong>
                  <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                    Chat interno — não vai pro cliente.
                  </div>
                </div>
                {badge(equipeNaoLidas)}
                <i className="bi bi-chevron-right" style={{ color: 'var(--portal-muted)' }} />
              </button>
            )}
            {carregandoLista && grupos.length === 0 ? (
              <div style={{ padding: 16, fontSize: '0.85rem', color: 'var(--portal-muted)' }}>Carregando grupos...</div>
            ) : grupos.length === 0 ? (
              <div style={{ padding: 16, fontSize: '0.85rem', color: 'var(--portal-muted)' }}>Nenhum grupo. O número precisa participar de algum grupo no WhatsApp.</div>
            ) : grupos.map((g) => (
              <button key={g.id} type="button" onClick={() => setAtivo(g)} style={listaItemStyle}>
                <div style={{ ...avatarStyle, background: corDoNome(g.subject) }}>
                  {g.picture_url ? <img src={g.picture_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : iniciais(g.subject)}
                </div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <strong style={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{g.subject || 'Grupo'}</strong>
                  <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.ultima_preview || 'Sem mensagens ainda'}</div>
                </div>
                {badge(naoLidas[g.id])}
                <i className="bi bi-chevron-right" style={{ color: 'var(--portal-muted)' }} />
              </button>
            ))}
          </div>
        ) : (
          <>
            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'clip', padding: 14, background: 'var(--portal-chat-bg)' }}>
              {mensagens.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem', marginTop: 20 }}>Sem mensagens ainda.</div>
              ) : mensagens.map((m) => (
                <GroupBubble
                  key={m.id}
                  mensagem={m}
                  isMobile={EH_TOUCH}
                  onAbrirImagem={onAbrirImagem}
                  onReagir={reagir}
                  onResponder={responder}
                  onEditar={editarInline}
                  onApagar={apagar}
                  onAbrirAcoes={(m, retangulo) => { setAcaoMensagem(m); setAcaoAncora(retangulo || null); }}
                  onIrParaMensagem={irParaMensagem}
                  destacada={destaqueId === m.id}
                />
              ))}
            </div>
            <form onSubmit={enviar} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10, borderTop: '1px solid var(--portal-border)', flexShrink: 0 }}>
              {replyTo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, background: 'rgba(61,139,55,0.1)', borderLeft: '3px solid #3D8B37', fontSize: '0.82rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: '#3D8B37', marginBottom: 1 }}>
                      <i className="bi bi-reply me-1" />
                      {replyTo.direcao === 'out' ? (replyTo.autor || 'você') : (replyTo.participante_nome || 'participante')}
                    </div>
                    <div style={{ opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {replyTo.conteudo ? String(replyTo.conteudo).slice(0, 100) : `[${replyTo.tipo}]`}
                    </div>
                  </div>
                  <button type="button" onClick={() => setReplyTo(null)} title="Cancelar resposta" style={{ background: 'transparent', border: 'none', color: 'var(--portal-muted)', cursor: 'pointer', padding: 2, flexShrink: 0 }}><i className="bi bi-x-lg" /></button>
                </div>
              )}
              {arquivo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 8, background: 'rgba(61,139,55,0.12)', border: '1px solid rgba(61,139,55,0.4)', fontSize: '0.82rem' }}>
                  <i className="bi bi-paperclip" style={{ color: '#3D8B37' }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{arquivo.name}</span>
                  <button type="button" onClick={() => setArquivo(null)} style={{ background: 'transparent', border: 'none', color: 'var(--portal-muted)', cursor: 'pointer' }}><i className="bi bi-x-lg" /></button>
                </div>
              )}
              {gravando ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" onClick={cancelarGrav} title="Cancelar" style={{ background: 'transparent', border: '1px solid var(--portal-border)', color: 'var(--portal-text)', borderRadius: 10, padding: '0 12px', height: 40, cursor: 'pointer' }}><i className="bi bi-x-lg" /></button>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', height: 40, borderRadius: 10, background: 'rgba(231,76,60,0.08)', color: '#e5484d', fontSize: '0.9rem' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#e5484d' }} /> Gravando… {fmtGrav(tempoGrav)}
                  </div>
                  <button type="button" onClick={pararEnviar} aria-label="Enviar áudio" style={{ background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}><i className="bi bi-send-fill" /></button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={inputArqRef} type="file" style={{ display: 'none' }} onChange={escolherArquivo} />
                  {/* Mesma câmera da conversa 1:1. Fotografar o produto e jogar
                      no grupo da equipe é o mesmo gesto — deixar o atalho só de
                      um lado só faria procurar onde não tem. */}
                  <input ref={inputCamRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={escolherArquivo} />
                  <div ref={anexoMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setAnexoMenu((v) => !v)} title="Enviar contato ou localização"
                      style={{ background: 'transparent', border: '1px solid var(--portal-border)', color: 'var(--portal-text)', borderRadius: 10, padding: '0 12px', height: 40, cursor: 'pointer' }}>
                      <i className={`bi ${anexoMenu ? 'bi-x-lg' : 'bi-plus-lg'}`} />
                    </button>
                    {anexoMenu && (
                      <div style={{ position: 'absolute', bottom: 48, left: 0, zIndex: 30, minWidth: 180, background: 'var(--portal-surface)', border: '1px solid var(--portal-border)', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                        <button type="button" onClick={anexarContato} style={anexoItemStyle}><i className="bi bi-person-fill" style={{ color: '#3D8B37', width: 20 }} /> Contato</button>
                        <button type="button" onClick={anexarLocalizacao} style={anexoItemStyle}><i className="bi bi-geo-alt-fill" style={{ color: '#3D8B37', width: 20 }} /> Localização</button>
                      </div>
                    )}
                  </div>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => inputArqRef.current?.click()} title="Anexar" style={{ background: 'transparent', border: '1px solid var(--portal-border)', color: 'var(--portal-text)', borderRadius: 10, padding: '0 12px', cursor: 'pointer' }}><i className="bi bi-paperclip" /></button>
                  {/* Só no dedo: `capture` é ignorado no computador e o botão
                      viraria um segundo clipe. */}
                  {EH_TOUCH && (
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => inputCamRef.current?.click()} title="Tirar foto" style={{ background: 'transparent', border: '1px solid var(--portal-border)', color: 'var(--portal-text)', borderRadius: 10, padding: '0 12px', cursor: 'pointer' }}><i className="bi bi-camera-fill" /></button>
                  )}
                  <textarea ref={textareaRef} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder={arquivo ? 'Legenda (opcional)...' : (replyTo ? 'Responder...' : 'Mensagem pro grupo...')} rows={1}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(e); } }}
                    style={{ flex: 1, resize: 'none', minHeight: 38, maxHeight: 120, padding: '8px 12px', borderRadius: 10, border: '1px solid var(--portal-border)', background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.9rem' }} />
                  {(texto.trim() || arquivo) ? (
                    // Segura o foco no campo: sem isto o toque passava o foco pro
                    // botão e o teclado fechava a cada envio (mesmo conserto da
                    // conversa 1:1). Comentário com `//` porque aqui é expressão
                    // (dentro do `? (`), não filho de JSX.
                    <button type="submit" disabled={enviando} onMouseDown={(e) => e.preventDefault()} aria-label="Enviar" style={{ background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', opacity: enviando ? 0.55 : 1 }}><i className="bi bi-send-fill" /></button>
                  ) : (
                    <button type="button" onClick={iniciarGravacao} disabled={enviando} aria-label="Gravar áudio" style={{ background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}><i className="bi bi-mic-fill" /></button>
                  )}
                </div>
              )}
            </form>
          </>
        )}
      </div>
  );

  return (
    <>
    {comoTela ? corpo : (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
        {corpo}
      </div>
    )}

    {/* Overlays fora do backdrop pra não fechar o modal ao dispensá-los. */}
    <AcoesMensagemMobile
      mensagem={acaoMensagem}
      ancora={acaoAncora}
      onFechar={() => { setAcaoMensagem(null); setAcaoAncora(null); }}
      onReagir={reagir}
      onResponder={responder}
      onEditar={editarPrompt}
      onApagar={apagar}
      onCopiar={copiar}
    />
    <ContatoPickerModal aberto={pickerContato} onClose={() => setPickerContato(false)} onEscolher={enviarContatoEscolhido} />
    <EditorImagem
      arquivo={editandoImagem}
      onCancelar={() => setEditandoImagem(null)}
      onPronto={(pronta) => {
        setEditandoImagem(null);
        setArquivo(pronta);
        setTimeout(() => textareaRef.current?.focus(), 50);
      }}
    />
    </>
  );
}

const listaItemStyle = { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--portal-border)', cursor: 'pointer', color: 'var(--portal-text)' };
const avatarStyle = { width: 42, height: 42, borderRadius: '50%', flexShrink: 0, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden' };
